import { MutableRefObject } from 'react';
import type { Editor, NodeWithPos, Range } from '@tiptap/core';
import { Fragment, type Node as PMNode, type ResolvedPos } from '@tiptap/pm/model';
import { type EditorState, NodeSelection } from '@tiptap/pm/state';
import { v4 as uuidv4 } from 'uuid';

import { ETiptapSelectorKey } from '../../../types/tiptap.types';
import { TEMPLATE_FIELD_BLOCK_NAME, TEMPLATE_FIELD_DROPDOWN_NAME } from '../../../doc-engine/templateFields';
import type { TTransformFragment } from '../../extensions/PasteRule';
import { isSelectionInField } from '../../extensions/TemplateLocking';
import { ZWSP } from '../../plugins/zwsp';
import type { TFieldNodeMatch } from '../../utils/findNodeByFieldId';
import isEmptyParagraph from '../../utils/isEmptyParagraph';
import selectNodeByFieldId from '../../utils/selectNodeByFieldId';
import { SECTION_NODE_NAME } from '../section/constants';
import { NEW_DROPDOWN_OPTIONS } from '../templateField/dropdownFieldUtils';
import { FORM_FIELD_LINE_NODE_NAME, FORM_FIELD_NODE_NAME } from './constants';

export type TDirection = 1 | -1;

/** Body blocks are the children of the document and of a landscape section; the section itself is not one. */
export const isBodyBlockParent = (node: PMNode): boolean =>
  node.type.name === SECTION_NODE_NAME || node.type === node.type.schema.topNodeType;

export const findBodyBlock = ($pos: ResolvedPos): NodeWithPos | undefined => {
  for (let depth = 1; depth <= $pos.depth; depth++) {
    if (!isBodyBlockParent($pos.node(depth))) return { node: $pos.node(depth), pos: $pos.before(depth) };
  }
  return undefined;
};

/** After a body block, or in its place when it is an empty paragraph: the line the author meant to fill. */
export const getRangeAfterBlock = (doc: PMNode, blockPos: number): Range => {
  const block = doc.nodeAt(blockPos)!;
  const end = blockPos + block.nodeSize;
  return isEmptyParagraph(block) ? { from: blockPos, to: end } : { from: end, to: end };
};

/** Where a new card goes from the toolbar: after the body block that holds or is the selection. */
export const getRangeAtSelection = ({ selection, doc }: EditorState): Range => {
  const block =
    selection instanceof NodeSelection && isBodyBlockParent(selection.$from.parent)
      ? { node: selection.node, pos: selection.from }
      : findBodyBlock(selection.$from);
  return block ? getRangeAfterBlock(doc, block.pos) : { from: selection.to, to: selection.to };
};

// A new field has a fresh id and no answer; a Dropdown starts with the default options.
const newField = (fieldName: string, optionsRef: MutableRefObject<Record<string, string[]>>) => {
  const fieldId = uuidv4();
  if (fieldName === TEMPLATE_FIELD_DROPDOWN_NAME) optionsRef.current[fieldId] = [...NEW_DROPDOWN_OPTIONS];
  return { type: fieldName, attrs: { fieldId } };
};

/** A new card with a `fieldName` field at `range`; its label takes the focus. */
export const insertFormField = (
  editor: Editor,
  range: Range,
  fieldName: string,
  optionsRef: MutableRefObject<Record<string, string[]>>
) => {
  const field = newField(fieldName, optionsRef);
  // Content text sits in the card itself, Inline text and Dropdown on its line.
  const cardContent =
    fieldName === TEMPLATE_FIELD_BLOCK_NAME
      ? { ...field, content: [{ type: ETiptapSelectorKey.PARAGRAPH }] }
      : { type: FORM_FIELD_LINE_NODE_NAME, content: [field] };
  editor.storage[FORM_FIELD_NODE_NAME].focusLabelOfFieldId = field.attrs.fieldId;
  editor.commands.insertContentAt(range, { type: FORM_FIELD_NODE_NAME, content: [cardContent] });
};

/** A collapsed caret in a paragraph's text and outside every field: where a field can join the paragraph. */
export const canInsertInParagraph = (state: EditorState): boolean => {
  const { $from, empty } = state.selection;
  return empty && $from.parent.type.name === ETiptapSelectorKey.PARAGRAPH && !isSelectionInField(state);
};

/** Where a paragraph's + adds a field to its text: a caret that can take one in that paragraph, or else its end. */
export const getParagraphInsertPos = (state: EditorState, paragraphPos: number): number => {
  const end = paragraphPos + state.doc.nodeAt(paragraphPos)!.nodeSize - 1;
  const { from } = state.selection;
  return from > paragraphPos && from <= end && canInsertInParagraph(state) ? from : end;
};

/** A new Inline text or Dropdown field in a paragraph, at `pos`, selected so that its popup asks for the label. */
export const insertParagraphField = (
  editor: Editor,
  fieldName: string,
  optionsRef: MutableRefObject<Record<string, string[]>>,
  pos: number
) => {
  const field = newField(fieldName, optionsRef);
  editor.storage[FORM_FIELD_NODE_NAME].focusLabelOfFieldId = field.attrs.fieldId;
  // No focus(): it gives the editor the focus a frame later, taking it from the popup's Label.
  editor.commands.insertContentAt(pos, field);
  selectNodeByFieldId(editor, field.attrs.fieldId);
};

export const isFieldInCard = (doc: PMNode, fieldPos: number): boolean => {
  const parent = doc.resolve(fieldPos).parent.type.name;
  return parent === FORM_FIELD_NODE_NAME || parent === FORM_FIELD_LINE_NODE_NAME;
};

/** The field a card holds, directly or on its line, or undefined when the card lost it. */
export const getWrappedField = (card: PMNode, cardPos: number): TFieldNodeMatch | undefined => {
  const child = card.firstChild;
  if (child?.type.name !== FORM_FIELD_LINE_NODE_NAME) return child ? { node: child, pos: cardPos + 1 } : undefined;
  return child.firstChild ? { node: child.firstChild, pos: cardPos + 2 } : undefined;
};

/** The `nodeName` field the selection is on or in, unless that field sits in a card, which holds its own settings. */
export const findSelectedField = ({ selection }: EditorState, nodeName: string): TFieldNodeMatch | undefined => {
  const { $from } = selection;
  let found: TFieldNodeMatch | undefined;
  if (selection instanceof NodeSelection) {
    if (selection.node.type.name === nodeName) found = { node: selection.node, pos: selection.from };
  } else {
    for (let depth = $from.depth; depth > 0 && !found; depth--) {
      if ($from.node(depth).type.name === nodeName) found = { node: $from.node(depth), pos: $from.before(depth) };
    }
  }
  return found && !isFieldInCard($from.doc, found.pos) ? found : undefined;
};

/** A copy of the card right after it, with fresh ids as a paste gives; the copy's label takes the focus. */
export const duplicateFormField = (editor: Editor, card: PMNode, cardPos: number, copyFields: TTransformFragment) => {
  const copy = copyFields(Fragment.from(card), editor.schema).firstChild!;
  editor.storage[FORM_FIELD_NODE_NAME].focusLabelOfFieldId = getWrappedField(copy, 0)?.node.attrs.fieldId;
  editor.commands.insertContentAt(cardPos + card.nodeSize, copy);
};

/** Whether a caret in a field is at its edge in `dir`; an empty field holds only the ZWSP caret anchor, so at both. */
export const isAtFieldEdge = ($inside: ResolvedPos, dir: TDirection): boolean =>
  $inside.parent.textContent === ZWSP || $inside.parentOffset === (dir > 0 ? $inside.parent.content.size : 0);

/** Where Enter leaves a card for a new paragraph: right after the card holding `$pos`, or undefined outside one. */
export const getFormFieldExitPos = ($pos: ResolvedPos): number | undefined => {
  for (let depth = $pos.depth; depth > 0; depth--) {
    if ($pos.node(depth).type.name === FORM_FIELD_NODE_NAME) return $pos.after(depth);
  }
  return undefined;
};
